import { useEffect, useState } from "react";
import { BookOpen, Moon, Users, Volume2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

/**
 * StudySpacesCard — library seats, 24/7 access, silent zones, group
 * rooms for the Teaching tab. Renders a compact editorial card:
 *
 *   Flagship name → total seats · 24/7 seats
 *   Chips: Silent · Group rooms · N libraries
 *
 * Hides gracefully when the curated seed doesn't cover this uni.
 */
export default function StudySpacesCard({ uniId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/universities/${uniId}/study-spaces`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [uniId]);

  const s = data?.study_spaces;
  if (!s) return null;

  const twentyFourSevenPct = s.seats
    ? Math.min(100, Math.round((s.seats_24_7 / s.seats) * 100))
    : 0;

  return (
    <div className="border border-[#002147]/10 bg-white/40 p-5" data-testid="study-spaces-card">
      <div className="flex items-baseline justify-between mb-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#7C3AED] inline-flex items-center gap-1.5">
          <BookOpen className="h-3 w-3" /> Study spaces
        </div>
        {s.source_url && (
          <a
            href={s.source_url}
            target="_blank" rel="noopener noreferrer"
            className="font-mono text-[10px] uppercase tracking-widest text-[#7C3AED] hover:underline inline-flex items-center gap-1"
          >
            Library site <ExternalLink className="h-2.5 w-2.5" />
          </a>
        )}
      </div>

      <div className="font-serif text-lg text-[#002147] leading-tight mb-4">
        {s.flagship}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#002147]/60">
            Total seats
          </div>
          <div className="font-serif text-2xl text-[#002147] leading-none mt-1">
            {s.seats.toLocaleString()}
          </div>
          <div className="font-mono text-[10px] text-[#002147]/50 mt-1">
            across {s.libraries} librar{s.libraries === 1 ? "y" : "ies"}
          </div>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#002147]/60 inline-flex items-center gap-1">
            <Moon className="h-2.5 w-2.5" /> 24/7 seats
          </div>
          <div className="font-serif text-2xl text-[#002147] leading-none mt-1">
            {s.seats_24_7.toLocaleString()}
          </div>
          <div className="font-mono text-[10px] text-[#002147]/50 mt-1">
            {twentyFourSevenPct}% of total round-the-clock
          </div>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-[#002147]/10 flex flex-wrap gap-2">
        {s.has_silent_zones && (
          <span className="inline-flex items-center gap-1 border border-[#002147]/20 text-[#002147]/70 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider">
            <Volume2 className="h-2.5 w-2.5" /> Silent zones
          </span>
        )}
        {s.has_group_rooms && (
          <span className="inline-flex items-center gap-1 border border-[#002147]/20 text-[#002147]/70 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider">
            <Users className="h-2.5 w-2.5" /> Group rooms
          </span>
        )}
      </div>
    </div>
  );
}
